import React, { useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import {
  ROUTE_ADMIN,
  ROUTE_ADMIN_BASE,
  ROUTE_AUTH_CALLBACK,
  ROUTE_BANK_ACCOUNT,
  ROUTE_DASHBOARD,
  ROUTE_KYC,
  ROUTE_LOGIN,
  ROUTE_ORDERS,
  ROUTE_TRANSACTIONS,
  ROUTE_USERS,
  ROUTE_WALLETS,
  ROUTE_DEPOSIT,
  ROUTE_SETTINGS,
  ROUTE_WAITING_LIST,
  ROUTE_PRESALE_ORDERS,
  ROUTE_PRESALE_TOKEN,
  ROUTE_PRESALE_USERS,
  // ROUTE_CRON_JOBS,
  ROUTE_REFERRALS,
  ROUTE_SUPPORT,
  ROUTE_WITHDRAW,
  ROUTE_LOGS,
  ROUTE_NOTIFICATIONS,
  ROUTE_MANAGEMENT,
} from './utils/routes';
import { ROUTE_PERMISSIONS } from './utils/permissions';
import { useTheme } from './useTheme';

import { Container } from 'src/components/Container';
import AdminPage from 'src/components/AdminPage';
import AdminIndexRedirect from 'src/components/AdminIndexRedirect';
import Notification from 'src/components/Notification';

import { useDispatch } from 'src/store/useDispatch';
import { fetchAdminProfile } from 'src/store/slice/adminSession';

import LogIn from './pages/LogIn';
import CallbackPage from './pages/Callback';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Kyc from './pages/Kyc';
import BankAccounts from './pages/BankAccounts';
import Transactions from './pages/Transactions';
import Orders from './pages/Orders';
import Wallets from './pages/Wallets';
import { ManualDeposits, ManualWithdrawals } from './pages/ManualTransactions';
import Settings from './pages/Settings';
import WaitingList from './pages/WaitingList';
import Home from './pages/Home/Home';
import PresaleTransactions from './pages/PresaleOrders';
import PresaleTokens from './pages/PresaleToken';
import PresaleUsers from './pages/PresaleUsers';
// import CronJobs from './pages/CronJobs';
import Referrals from './pages/Referrals';
import Support from './pages/Support';
import Logs from './pages/Logs';
import Notifications from './pages/Notifications';
import Management from './pages/Management';

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { message, isSuccess } = useSelector(({ appAlert }) => appAlert);
  const { initialized } = useSelector(({ adminSession }) => adminSession);
  const theme = useTheme();

  useEffect(() => {
    const isPublicRoute =
      location.pathname === ROUTE_LOGIN || location.pathname.startsWith('/auth/callback');

    if (isPublicRoute) {
      return;
    }

    const access = localStorage?.getItem('access');

    if (!access) {
      navigate(ROUTE_LOGIN);
      return;
    }

    if (!initialized) {
      dispatch(fetchAdminProfile());
    }
  }, [navigate, location.pathname, dispatch, initialized]);

  return (
    <ThemeProvider theme={theme}>
      <Container
        height="100vh"
        minHeight="100dvh"
        display="flex"
        flexDirection="column"
        color={theme.dark}
        background={theme.lightGrey}
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path={ROUTE_LOGIN} element={<LogIn />} />
          <Route path={ROUTE_AUTH_CALLBACK} element={<CallbackPage />} />
          <Route path={ROUTE_ADMIN_BASE} element={<AdminIndexRedirect />} />
          <Route path={ROUTE_ADMIN}>
            <Route path={ROUTE_DASHBOARD} element={<AdminPage Component={Dashboard} permission={ROUTE_PERMISSIONS[ROUTE_DASHBOARD]} />} />
            <Route
              path={`${ROUTE_USERS}/*`}
              element={<AdminPage Component={Users} permission={ROUTE_PERMISSIONS[ROUTE_USERS]} />}
            />
            <Route path={ROUTE_KYC} element={<AdminPage Component={Kyc} permission={ROUTE_PERMISSIONS[ROUTE_KYC]} />} />
            <Route path={ROUTE_WAITING_LIST} element={<AdminPage Component={WaitingList} permission={ROUTE_PERMISSIONS[ROUTE_WAITING_LIST]} />} />
            <Route path={ROUTE_PRESALE_ORDERS} element={<AdminPage Component={PresaleTransactions} permission={ROUTE_PERMISSIONS[ROUTE_PRESALE_ORDERS]} />} />
            <Route path={ROUTE_PRESALE_TOKEN} element={<AdminPage Component={PresaleTokens} permission={ROUTE_PERMISSIONS[ROUTE_PRESALE_TOKEN]} />} />
            <Route path={ROUTE_PRESALE_USERS} element={<AdminPage Component={PresaleUsers} permission={ROUTE_PERMISSIONS[ROUTE_PRESALE_USERS]} />} />
            {/* <Route path={ROUTE_CRON_JOBS} element={<AdminPage Component={CronJobs} permission={ROUTE_PERMISSIONS[ROUTE_CRON_JOBS]} />} /> */}
            <Route path={ROUTE_BANK_ACCOUNT} element={<AdminPage Component={BankAccounts} permission={ROUTE_PERMISSIONS[ROUTE_BANK_ACCOUNT]} />} />
            <Route path={ROUTE_TRANSACTIONS} element={<AdminPage Component={Transactions} permission={ROUTE_PERMISSIONS[ROUTE_TRANSACTIONS]} />} />
            <Route path={ROUTE_ORDERS} element={<AdminPage Component={Orders} permission={ROUTE_PERMISSIONS[ROUTE_ORDERS]} />} />
            <Route path={ROUTE_WALLETS} element={<AdminPage Component={Wallets} permission={ROUTE_PERMISSIONS[ROUTE_WALLETS]} />} />
            <Route path={ROUTE_DEPOSIT} element={<AdminPage Component={ManualDeposits} permission={ROUTE_PERMISSIONS[ROUTE_DEPOSIT]} />} />
            <Route path={ROUTE_WITHDRAW} element={<AdminPage Component={ManualWithdrawals} permission={ROUTE_PERMISSIONS[ROUTE_WITHDRAW]} />} />
            <Route path={ROUTE_SETTINGS} element={<AdminPage Component={Settings} permission={ROUTE_PERMISSIONS[ROUTE_SETTINGS]} />} />
            <Route path={ROUTE_REFERRALS} element={<AdminPage Component={Referrals} permission={ROUTE_PERMISSIONS[ROUTE_REFERRALS]} />} />
            <Route path={ROUTE_SUPPORT} element={<AdminPage Component={Support} permission={ROUTE_PERMISSIONS[ROUTE_SUPPORT]} />} />
            <Route path={ROUTE_LOGS} element={<AdminPage Component={Logs} permission={ROUTE_PERMISSIONS[ROUTE_LOGS]} />} />
            <Route path={ROUTE_NOTIFICATIONS} element={<AdminPage Component={Notifications} permission={ROUTE_PERMISSIONS[ROUTE_NOTIFICATIONS]} />} />
            <Route
              path={`${ROUTE_MANAGEMENT}/*`}
              element={<AdminPage Component={Management} permission={ROUTE_PERMISSIONS[ROUTE_MANAGEMENT]} />}
            />
          </Route>
        </Routes>
        {message && <Notification message={message} isSuccess={isSuccess} />}
      </Container>
    </ThemeProvider>
  );
};

export default App;
